/**
 * The left rail: which project, and which way of looking at it.
 *
 * Projects are listed with a live file count and total size, folded from the
 * change log, so the rail is never stale and never needs its own request.
 */
import { useMemo } from 'react'
import { Boxes, FolderTree, GitCommitVertical, MessagesSquare, Circle, Lock, FolderSync } from 'lucide-react'
import type { ProjectId } from '@lattice/core'
import { useWorkspace, filesByProject, locksHeldNow } from '@/store/workspace'
import { useUi, type View } from '@/store/ui'
import { formatBytes, pluralise } from '@/lib/format'
import { cn } from '@/lib/utils'
import { Tooltip } from '@/components/ui/primitives'

const VIEWS: { id: View; label: string; icon: typeof FolderTree; key: string }[] = [
  { id: 'files', label: 'Files', icon: FolderTree, key: '1' },
  { id: 'activity', label: 'Activity', icon: GitCommitVertical, key: '2' },
  { id: 'comments', label: 'Comments', icon: MessagesSquare, key: '3' },
  { id: 'locks', label: 'Locks', icon: Lock, key: '4' },
  { id: 'sync', label: 'Changes', icon: FolderSync, key: '5' },
]

export function Sidebar() {
  const projects = useWorkspace(s => s.projects)
  const changes = useWorkspace(s => s.changes)
  const comments = useWorkspace(s => s.comments)
  const { projectId, view, go, setView } = useUi()

  const files = useMemo(() => filesByProject(changes), [changes])

  const lockLog = useWorkspace(s => s.locks)
  const lockCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const lock of locksHeldNow(lockLog).values()) {
      counts.set(lock.projectId, (counts.get(lock.projectId) ?? 0) + 1)
    }
    return counts
  }, [lockLog])

  const commentCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const c of comments) {
      if (c.deletedAt) continue
      counts.set(c.projectId, (counts.get(c.projectId) ?? 0) + 1)
    }
    return counts
  }, [comments])

  return (
    <nav className="flex h-full w-56 shrink-0 flex-col border-r border-[var(--border)] bg-subtle">
      <div className="flex items-center gap-2 px-3 h-11 shrink-0">
        <Boxes className="h-4 w-4 text-[var(--fg)]" strokeWidth={1.75} />
        <span className="text-sm font-semibold tracking-tight">Lattice</span>
      </div>

      <div className="px-2 pb-1">
        <div className="px-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--fg-subtle)]">
          Projects
        </div>
        <div className="space-y-px">
          {projects.map(project => {
            const set = files.get(project.id as ProjectId)
            const count = set?.size ?? 0
            const bytes = set ? [...set.values()].reduce((sum, f) => sum + f.size, 0) : 0
            const active = projectId === project.id
            return (
              <Tooltip
                key={project.id}
                side="right"
                label={
                  <span>
                    <span className="font-medium">{project.name}</span>
                    {project.description && <span className="block text-[var(--fg-muted)]">{project.description}</span>}
                    <span className="block text-[var(--fg-subtle)]">
                      {count ? `${pluralise(count, 'file')} · ${formatBytes(bytes)}` : 'No files yet'}
                    </span>
                  </span>
                }
              >
                <button
                  onClick={() => go({ projectId: project.id as ProjectId, view: 'files', openPath: undefined })}
                  className={cn(
                    'snap group flex w-full items-center gap-2 rounded-[var(--radius-md)] px-1.5 py-1 text-left',
                    active ? 'bg-[var(--bg-active)] text-[var(--fg)]' : 'text-[var(--fg-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]',
                  )}
                >
                  <Circle
                    className={cn('h-1.5 w-1.5 shrink-0', count ? 'fill-current' : 'opacity-30')}
                    strokeWidth={0}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{project.name}</span>
                  <span className="tabular text-[10px] text-[var(--fg-subtle)]">{count || ''}</span>
                </button>
              </Tooltip>
            )
          })}
        </div>
      </div>

      {projectId && (
        <div className="mt-2 px-2">
          <div className="px-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--fg-subtle)]">
            View
          </div>
          <div className="space-y-px">
            {VIEWS.map(item => {
              const active = view === item.id
              const badge = item.id === 'comments' ? commentCounts.get(projectId)
                : item.id === 'locks' ? lockCounts.get(projectId)
                : undefined
              return (
                <Tooltip key={item.id} side="right" label={item.label} keys={[item.key]}>
                  <button
                    onClick={() => setView(item.id)}
                    className={cn(
                      'snap flex w-full items-center gap-2 rounded-[var(--radius-md)] px-1.5 py-1 text-left',
                      active ? 'bg-[var(--bg-active)] text-[var(--fg)]' : 'text-[var(--fg-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]',
                    )}
                  >
                    <item.icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                    <span className="flex-1 text-sm">{item.label}</span>
                    {badge ? <span className="tabular text-[10px] text-[var(--fg-subtle)]">{badge}</span> : null}
                  </button>
                </Tooltip>
              )
            })}
          </div>
        </div>
      )}

      <div className="flex-1" />
      <SyncBadge />
    </nav>
  )
}

/**
 * A quiet indicator of whether we are in touch with the server.
 *
 * It says "Offline" rather than showing an error, because being offline is a
 * normal state for a local-first product: everything still works, and the
 * change simply has not been shared yet.
 */
function SyncBadge() {
  const syncState = useWorkspace(s => s.syncState)
  const cursor = useWorkspace(s => s.cursor)

  const tone = syncState === 'offline' ? 'text-[var(--modified)]' : 'text-[var(--fg-subtle)]'
  const label = syncState === 'offline' ? 'Offline — changes are saved locally'
    : syncState === 'syncing' ? 'Syncing…'
    : 'Up to date'

  return (
    <Tooltip side="right" label={`${label} · cursor ${cursor}`}>
      <div className={cn('flex items-center gap-1.5 px-3.5 py-2 text-[11px]', tone)}>
        <span className={cn(
          'h-1.5 w-1.5 rounded-full',
          syncState === 'offline' ? 'bg-[var(--modified)]' : 'bg-[var(--added)]',
          syncState === 'syncing' && 'animate-pulse',
        )} />
        {label}
      </div>
    </Tooltip>
  )
}
